import { useEffect, useRef, type ReactNode } from 'react';

export interface ModalProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** Read-only dialogs: a backdrop click or any key that is not navigation/activation closes it. */
  dismissive?: boolean;
  /** Review tables need room; forms keep the narrow default. */
  wide?: boolean;
}

const NAV_KEYS = new Set(['Tab', 'Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'Escape']);

function isActivation(e: React.KeyboardEvent): boolean {
  const el = e.target as HTMLElement;
  const interactive = el.closest('button, a[href], input, select, textarea');
  return interactive !== null && (e.key === 'Enter' || e.key === ' ');
}

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Wraps Tab / Shift+Tab inside the dialog; showModal() makes the page inert but lets focus leave to the browser UI. */
function trapTab(e: React.KeyboardEvent<HTMLDialogElement>): void {
  if (e.key !== 'Tab') return;
  const items = [...e.currentTarget.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
  const first = items[0];
  const last = items[items.length - 1];
  if (!first || !last) return;
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

/** Native <dialog>: focus trap, Escape to close, backdrop. */
export function Modal(props: ModalProps): React.JSX.Element {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (props.open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!props.open && el.open) {
      el.close();
    }
  }, [props.open]);
  return (
    <dialog
      ref={ref}
      className={props.wide ? 'modal modal--wide' : 'modal'}
      aria-labelledby="modal-title"
      onClose={props.onClose}
      onKeyDown={(e) => {
        trapTab(e);
        if (props.dismissive && !NAV_KEYS.has(e.key) && !isActivation(e)) props.onClose();
      }}
      onClick={(e) => {
        if (props.dismissive && e.target === e.currentTarget) props.onClose();
      }}
      onCancel={(e) => {
        e.preventDefault();
        props.onClose();
      }}
    >
      {props.open && (
        <>
          <h2 className="modal__title" id="modal-title">
            {props.title}
          </h2>
          {props.children}
          {props.footer && <div className="modal__footer">{props.footer}</div>}
        </>
      )}
    </dialog>
  );
}
